import type { DdqSessionQuestion } from '@/types/api.types';

/**
 * The three filter pills exposed by the Q&A index panel. `all` shows
 * everything (the product default for now), `needs-review` surfaces items that
 * still want human scrutiny, and `approved` shows items the reviewer has
 * already accepted or modified.
 */
export type WorkspaceFilter = 'all' | 'needs-review' | 'approved';

/**
 * Whether a question still needs reviewer scrutiny. An item needs review when
 * it has not yet been actioned (Pending) AND it is the kind of answer a
 * reviewer should look at: an AI draft (`Generated` tier / `llm` source) or a
 * low-confidence / unmatched result. Confident, already-grounded answers that
 * remain Pending are not flagged as needing scrutiny — they are easy accepts.
 */
export function needsReview(q: DdqSessionQuestion): boolean {
  const action = q.userAction ?? 'Pending';
  if (action !== 'Pending') {
    return false;
  }
  const isAiDraft = q.response_tier === 'Generated' || q.answer_source === 'llm';
  const isUnmatched = q.response_tier === 'NoMatch';
  const isLowConfidence = q.confidence < 90;
  return isAiDraft || isUnmatched || isLowConfidence;
}

/**
 * Whether a question counts as approved — the reviewer has committed to it by
 * accepting or modifying the answer.
 */
export function isApproved(q: DdqSessionQuestion): boolean {
  const action = q.userAction ?? 'Pending';
  return action === 'Accepted' || action === 'Modified';
}

/**
 * Apply the active pill filter and a free-text search to the question list.
 * Search matches the question text and the answer snippet, mirroring the
 * review feature's `filterQuestions`.
 */
export function filterWorkspaceQuestions(
  questions: DdqSessionQuestion[],
  filter: WorkspaceFilter,
  search: string,
): DdqSessionQuestion[] {
  let filtered = questions;

  if (filter === 'needs-review') {
    filtered = filtered.filter(needsReview);
  } else if (filter === 'approved') {
    filtered = filtered.filter(isApproved);
  }

  const query = search.trim().toLowerCase();
  if (query) {
    filtered = filtered.filter(
      (q) =>
        q.incoming_question.toLowerCase().includes(query) ||
        (q.userResponse ?? q.suggested_response ?? '').toLowerCase().includes(query),
    );
  }

  return filtered;
}
